/** Customer analytics API — C1–C11 + diagnostics. */
import { http } from "@/lib/api";
import { thresholds } from "@/lib/thresholds";

export interface BillLine {
  label: string;
  from_qty: number;
  to_qty: number | null;
  consumed_qty: number;
  rate_per_unit: number;
  amount: number;
}

export interface ShadowBill {
  sensor_id: string;
  house_id: string | null;
  period_days: number;
  consumption_m3: number;
  lines: BillLine[];
  water_charge: number;
  sewerage_charge: number;
  total: number;
  current_tier: string;
  min_charge_applied: boolean;
  currency: string;
}

export interface TierAlert {
  sensor_id: string;
  consumption_m3: number;
  current_tier: string;
  next_tier: string | null;
  to_next_tier_m3: number | null;
  status: "OK" | "WARN" | "CRITICAL" | string;
}

export interface PeerComparison {
  sensor_id: string;
  area_marla: number | null;
  consumption_m3: number;
  cohort_avg_m3: number;
  cohort_p25_m3: number;
  cohort_median_m3: number;
  cohort_p75_m3: number;
  cohort_size: number;
  percentile: number;
  verdict: "below" | "average" | "above" | string;
}

export interface WindowShare {
  window: string;
  consumption_m3: number;
  percent: number;
}
export interface ConsumptionProfile {
  sensor_id: string;
  total_m3: number;
  days: number;
  windows: WindowShare[];
}

export interface ConsumptionTrend {
  sensor_id: string;
  previous_m3: number;
  current_m3: number;
  change_pct: number;
  previous_temp_c: number | null;
  current_temp_c: number | null;
}

export interface BillProjection {
  sensor_id: string;
  daily_avg_m3: number;
  consumed_m3: number;
  projected_m3: number;
  projected_bill: number;
  current_tier: string;
  projected_tier: string;
  elapsed_days: number;
  days_remaining: number;
  cycle_progress_pct: number;
  lines: BillLine[];
}

export interface Saving {
  label: string;
  reduce_m3: number;
  save_pkr: number;
}
export interface SavingsRecommendations {
  sensor_id: string;
  marginal_rate_pkr_m3: number;
  recommendations: Saving[];
}

export interface BudgetGoal {
  sensor_id: string;
  target_m3: number;
  target_pkr: number;
  projected_m3: number;
  gap_m3: number;
  status: "ahead" | "on_track" | "behind" | string;
  progress_pct: number;
}

export interface BillPeerComparison {
  sensor_id: string;
  area_marla: number | null;
  subject_bill: number;
  cohort_p25_pkr: number;
  median_bill: number;
  cohort_p75_pkr: number;
  savings_vs_median: number;
  cohort_size: number;
}

export interface Diagnostics {
  sensor_id: string;
  observed_m3: number;
  expected_m3: number;
  excess_m3: number;
  status: "normal" | "elevated" | "high" | string;
  attribution: Record<string, number>;
}

const enc = encodeURIComponent;
async function get<T>(id: string, path: string, s?: AbortSignal): Promise<T> {
  const { data } = await http.get<T>(`/customer/${enc(id)}/${path}`, { signal: s });
  return data;
}

export const fetchShadowBill = (id: string, s?: AbortSignal) =>
  get<ShadowBill>(id, "shadow-bill", s);
export const fetchTierAlert = (id: string, s?: AbortSignal) =>
  get<TierAlert>(id, "tier-alert", s);
export const fetchPeerComparison = (id: string, s?: AbortSignal) =>
  get<PeerComparison>(id, "peer-comparison", s);
export const fetchConsumptionProfile = (id: string, s?: AbortSignal) =>
  get<ConsumptionProfile>(id, "consumption-profile", s);
export const fetchConsumptionTrend = (id: string, s?: AbortSignal) =>
  get<ConsumptionTrend>(id, "consumption-trend", s);
export const fetchBillProjection = (id: string, s?: AbortSignal) =>
  get<BillProjection>(id, "bill-projection", s);
export const fetchSavings = (id: string, s?: AbortSignal) =>
  get<SavingsRecommendations>(id, "savings-recommendation", s);
export const fetchBudgetGoal = (id: string, s?: AbortSignal) =>
  get<BudgetGoal>(id, "budget-goal", s);
export const fetchBillPeer = (id: string, s?: AbortSignal) =>
  get<BillPeerComparison>(id, "bill-peer-comparison", s);
export const fetchDiagnostics = (id: string, s?: AbortSignal) =>
  get<Diagnostics>(id, "diagnostics", s);

// ── Metering priority (fleet-wide, not per-sensor — distinct endpoint shape) ──
export interface MeteringPriorityRow {
  house_id: string;
  sensor_id: string;
  locality: string;
  user_type: string;
  avg_monthly_m3: number | null;
  report_days: number;
  tier: string;
}

export interface MeteringPriorityBand {
  tier: string;
  min_m3: number | null;
  max_m3: number | null;
  count: number;
  share: number;
}

export interface MeteringPrioritySummary {
  total_houses: number;
  as_of: string;
  bands: MeteringPriorityBand[];
}

export interface MeteringPriorityFleet {
  rows: MeteringPriorityRow[];
  summary: MeteringPrioritySummary;
}

export const fetchMeteringPriority = (
  days = thresholds().default_analysis_days,
  locality?: string | null,
  s?: AbortSignal,
) =>
  http
    .get<MeteringPriorityFleet>("/customer/metering-priority", {
      params: { days, ...(locality ? { locality } : {}) },
      signal: s,
    })
    .then((r) => r.data);
